<template>
    <Dialog
        :visible="visible"
        modal
        header="Resolve your enrolments first"
        :style="{ width: 'min(520px, 92vw)' }"
        @update:visible="$emit('update:visible', $event)"
    >
        <div class="plan-blocked-body flex flex-column gap-3 py-2" :class="`is-${resolvedVariant}`">
            <div class="flex align-items-center gap-3">
                <i class="pi pi-exclamation-triangle blocked-icon" aria-hidden="true"></i>
                <p class="text-600 m-0">
                    The <strong>{{ packageName }}</strong> plan doesn't cover everything you're
                    currently using. Please resolve the items below, then switch again.
                </p>
            </div>

            <div v-for="(blocker, i) in blockers" :key="i" class="blocked-item">
                <div class="flex align-items-center justify-content-between gap-2 mb-1">
                    <span class="Paragraph-Medium">{{ labelFor(blocker.type) }}</span>
                    <span class="blocked-count">{{ blocker.current }} / {{ blocker.max }}</span>
                </div>
                <span class="Caption text-600">{{ blocker.message }}</span>
            </div>

            <span class="Caption text-600">
                Your current plan stays active — nothing has changed yet.
            </span>
        </div>

        <template #footer>
            <div class="plan-blocked-footer" :class="`is-${resolvedVariant}`">
                <Button label="Close" class="footer-btn footer-btn-outline"
                    @click="$emit('update:visible', false)" />
                <Button v-if="manageLabel" :label="manageLabel" :class="['footer-btn', solidButtonClass]"
                    @click="$emit('manage')" />
            </div>
        </template>
    </Dialog>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import { useAuthStore } from '@/stores/authStore';

/**
 * Shown when a plan change is refused because enrolments exceed the target plan.
 *
 * Enrolments cannot simply lock the way notes and notices do — the other side of the relationship
 * is a paying user who made no change — so the switch is blocked until the user reduces them.
 * Fed by GET /subscriptions/change-preview, and by the 409 from POST /subscriptions/change-package.
 */
const props = defineProps({
    visible: { type: Boolean, default: false },
    packageName: { type: String, default: 'selected' },
    blockers: { type: Array, default: () => [] },
    manageLabel: { type: String, default: '' },
    variant: {
        type: String,
        default: '',
        validator: (value) => ['', 'tutor', 'student', 'institute'].includes(value),
    },
});

defineEmits(['update:visible', 'manage']);

const VARIANTS = ['tutor', 'student', 'institute'];

const SOLID_BUTTON_CLASS = {
    tutor: 'teal-button-small',
    student: 'orange-button-small',
    institute: 'yellow-button-small',
};

const authStore = useAuthStore();

const resolvedVariant = computed(() => {
    if (VARIANTS.includes(props.variant)) return props.variant;
    const role = authStore.userType || authStore.user?.role;
    return VARIANTS.includes(role) ? role : 'institute';
});

const solidButtonClass = computed(() => SOLID_BUTTON_CLASS[resolvedVariant.value]);

const LABELS = {
    enrolledTutors: 'Enrolled tutors',
    enrolledStudents: 'Enrolled students',
};

const labelFor = (type) => LABELS[type] || type;
</script>

<style scoped>
/* Role accents — institute (yellow) is the default */
.plan-blocked-body,
.plan-blocked-footer {
    --plan-accent: #965903;
    --plan-accent-hover: #7a4903;
    --plan-icon: #d97706;
    --plan-chip-bg: #fde68a;
    --plan-chip-fg: #b45309;
}

/* tutor accent (green) */
.plan-blocked-body.is-tutor,
.plan-blocked-footer.is-tutor {
    --plan-accent: #005f5f;
    --plan-accent-hover: #003f3f;
    --plan-icon: #00786c;
    --plan-chip-bg: #c9f2e9;
    --plan-chip-fg: #00413a;
}

/* student accent (red) */
.plan-blocked-body.is-student,
.plan-blocked-footer.is-student {
    --plan-accent: #a72e44;
    --plan-accent-hover: #7c1f30;
    --plan-icon: #c2334d;
    --plan-chip-bg: #ffd9e0;
    --plan-chip-fg: #7c1f30;
}

/* Header "X" stays neutral — no accent border or focus ring */
:deep(.p-dialog-close-button),
:deep(.p-dialog-close-button:hover),
:deep(.p-dialog-close-button:active),
:deep(.p-dialog-close-button:focus),
:deep(.p-dialog-close-button:focus-visible) {
    border-color: transparent;
    box-shadow: none;
    outline: 0 none;
}

.blocked-icon {
    font-size: 1.7rem;
    color: var(--plan-icon);
    flex-shrink: 0;
}

.blocked-item {
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 6px;
    padding: 0.75rem 0.9rem;
    background: rgba(0, 0, 0, 0.02);
}

.blocked-count {
    font-weight: 700;
    font-size: 0.85rem;
    color: var(--plan-chip-fg);
    background: var(--plan-chip-bg);
    border-radius: 999px;
    padding: 0.1rem 0.6rem;
    white-space: nowrap;
}

.plan-blocked-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.5rem;
}

/* Both actions share the app's small-button metrics so they line up exactly */
.plan-blocked-footer .footer-btn {
    height: 32px;
    padding: 8px 16px;
    font-size: 14px;
    border-radius: 4px;
}

.plan-blocked-footer .footer-btn-outline {
    background-color: #ffffff;
    border: 1px solid var(--plan-accent);
    color: var(--plan-accent);
    transition: background-color 0.2s, border-color 0.2s, color 0.2s;
}

.plan-blocked-footer .footer-btn-outline:hover,
.plan-blocked-footer .footer-btn-outline:active,
.plan-blocked-footer .footer-btn-outline:focus {
    background-color: var(--plan-accent-hover) !important;
    border-color: var(--plan-accent-hover) !important;
    color: #ffffff !important;
}

@media (max-width: 480px) {
    .plan-blocked-footer .footer-btn {
        height: 28px;
        padding: 6px 12px;
        font-size: 12px;
    }
}
</style>
